import React, { useEffect, lazy, Suspense } from "react";
import { useDispatch } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// Import secure logging FIRST to override console methods before other imports
import "./utils/secureLogging";

import { auth } from "./firebase";
import { currentUser } from "./functions/auth";
import { useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import UseStyles from "./core/UseStyles"
import { jwtDecode } from "jwt-decode";
import { isGuestUser, clearAllGuestData } from "./utils/guestSession";
import { startTokenRefresh, stopTokenRefresh } from "./utils/tokenRefresh";
import PromptHelperWidget from "./components/PromptHelperWidget";
import useInactivityLogout from "./utils/useInactivityLogout";

import "./styles.css"
import CatalogProduct from "./pages/CatalogProduct";

const ForgotPass = lazy(() => import("./pages/auth/ForgotPass"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const VerifyOtp = lazy(() => import("./pages/auth/VerifyOtp"));
const VerifyOtpForgot = lazy(() => import("./pages/auth/VerifyOtpForgot"));
const Landing = lazy(() => import("./pages/Landing"));
const Products = lazy(() => import("./pages/Products"));
const FilteredProducts = lazy(() => import("./pages/FilteredProducts"));
const TestLanding = lazy(() => import("./pages/TestLanding"));
const TryNow = lazy(() => import("./pages/TryNow"));
const GiftingGuide = lazy(() => import("./pages/GiftingGuide"));
const HowToCustomTshirtAI = lazy(() => import("./pages/HowToCustomTshirtAI"));
const HowToPersonalizedGiftsAI = lazy(() => import("./pages/HowToPersonalizedGiftsAI"));
const HowToCustomMugAI = lazy(() => import("./pages/HowToCustomMugAI"));
const HowToPrintOnDemand = lazy(() => import("./pages/HowToPrintOnDemand"));
const HowToCustomMerchWithAI = lazy(() => import("./pages/HowToCustomMerchWithAI"));
const BestPromptingTechniquesAI = lazy(() => import("./pages/BestPromptingTechniquesAI"));
const CompareDTGVsDTF = lazy(() => import("./pages/CompareDTGVsDTF"));
const ComparePrintspaceAIVsCanvaMerch = lazy(() => import("./pages/ComparePrintspaceAIVsCanvaMerch"));
const GuidesHub = lazy(() => import("./pages/GuidesHub"));
const WhatIsPrintSpaceAI = lazy(() => import("./pages/WhatIsPrintSpaceAI"));
const Shop = lazy(() => import("./pages/Shop"));
const ShopDesignDetail = lazy(() => import("./pages/ShopDesignDetail"));
const CreatorProfile = lazy(() => import("./pages/CreatorProfile"));


const RegisterComplete = lazy(() => import("./pages/auth/RegisterComplete"));
const CategoryHome = lazy(() => import("./pages/category/CategoryHome"));
const Base = lazy(() => import("./core/Base"));
const AllFurnitures = lazy(() => import("./core/AllFurnitures"));
const MyDesigns = lazy(() => import("./core/MyDesigns"));
const AdminPage = lazy(() => import("./core/AdminPage"));

const CreateDesign = lazy(() => import("./core/CreateDesign"));
const DesignerTool = lazy(() => import("./core/DesignerTool"));


const NavBar = lazy(() => import("./core/navBar/NavBar"));
const ContactUs = lazy(() => import("./core/ContactUs"));
const Cart = lazy(() => import("./pages/CartImproved"));
const OrdersPage = lazy(() => import("./core/OrdersPage"));
const Privacy = lazy(() => import("./core/PrivacyPage"));




const Checkout = lazy(() => import("./pages/CheckoutImproved"));
const TokenCheckout = lazy(() => import("./core/TokenCheckout"));

const Address = lazy(() => import("./core/Address"));


const Faq = lazy(() => import("./core/Faq"));
const HowThingsWork = lazy(() => import("./pages/HowThingsWork"));
// const Privacy = lazy(() => import("./core/Privacy"));
const Tnc = lazy(() => import("./core/Tnc"));


const Footer = lazy(() => import("./core/Footer"));
const Product = lazy(()=> import( "./pages/Product"))


const App = () => {
  const classes = UseStyles();
  const dispatch = useDispatch();

  useInactivityLogout(dispatch);

  const routePath = useLocation();

  let role = null;
  const data = localStorage.getItem("userData")
    if (data) {
      const username = JSON.parse(data);
      const decoded = jwtDecode(username?.id_token); // Decode JWT
  
      role = decoded?.role;
    }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [routePath]);

  // Route-level canonical + crawl policy fallback (page-specific SeoHead can override).
  useEffect(() => {
    const pathnameRaw = routePath?.pathname || "/";
    const pathname =
      pathnameRaw.length > 1 && pathnameRaw.endsWith("/")
        ? pathnameRaw.slice(0, -1)
        : pathnameRaw;

    const nonIndexExactPaths = new Set([
      "/test",
      "/landing",
      "/login",
      "/register",
      "/forgotpassword",
      "/verifyotp",
      "/verifyotpforgot",
      "/register/complete",
      "/cart",
      "/checkout",
      "/token-checkout",
      "/address",
      "/orderspage",
      "/mydesigns",
    ]);

    const nonIndexPrefixes = [
      "/admin",
    ];

    const shouldNoIndex =
      nonIndexExactPaths.has(pathname) ||
      nonIndexPrefixes.some((p) => pathname.startsWith(p));

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://printspaceai.com${pathname}`);

    let robotsMeta = document.head.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", shouldNoIndex ? "noindex,nofollow" : "index,follow");
  }, [routePath]);

  // Global storage error handler
  useEffect(() => {
    const handleStorageError = (event) => {
      if (event.error && event.error.message && event.error.message.includes("storage")) {
        console.error("Global storage error detected:", event.error);
        // Show a toast notification
        if (window.toast) {
          window.toast.error("Storage issue detected. Please clear some browser data.");
        }
      }
    };

    // Listen for unhandled promise rejections (storage errors)
    window.addEventListener('unhandledrejection', handleStorageError);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleStorageError);
    };
  }, []);

  // to check firebase auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        // console.log("user", user);

        currentUser(idTokenResult.token)
          .then((res) => {
            dispatch({
              type: "LOGGED_IN_USER",
              payload: {
                name: res.data.name,
                email: res.data.email,
                token: idTokenResult.token,
                role: res.data.role,
                _id: res.data._id,
              },
            });
          })
          .catch((err) => console.log(err));
      }
    });
    // cleanup
    return () => unsubscribe();
  }, [dispatch]);

  // Token refresh management - start/stop based on login status
  useEffect(() => {
    const checkAndStartTokenRefresh = () => {
      const userData = localStorage.getItem("userData");
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          // Check if we have valid tokens (not just Firebase auth)
          if (parsed?.access_token && parsed?.refresh_token) {
            startTokenRefresh();
          } else {
            stopTokenRefresh();
          }
        } catch (error) {
          console.error("Error parsing userData:", error);
          stopTokenRefresh();
        }
      } else {
        stopTokenRefresh();
      }
    };

    // Check on mount and route changes
    checkAndStartTokenRefresh();

    // Also check periodically (in case userData changes externally)
    const interval = setInterval(checkAndStartTokenRefresh, 30000); // Every 30 seconds

    return () => {
      clearInterval(interval);
      stopTokenRefresh();
    };
  }, [routePath]);

  return (
    <Suspense fallback={
      <div style={{textAlign: 'center',padding:'40px'}}>Loading...</div>
    }>
      <div className={classes.background}>
        <div className={classes.wrap}>
          <NavBar />
          {/* <SideDrawer /> */}
          <ToastContainer />
          <Switch>
            {/* <Route path="/" exact component={Base} /> */}
            {/* <Route path="/" exact component={Login} /> */}
            {/* <Route exact path="/" component={Home} /> */}
            <Route path="/test" exact component={TestLanding} />
            <Route path="/landing" exact component={Landing} />
            <Route path="/" exact component={Landing} />
            <Route path="/try-now" exact component={TryNow} />
            <Route path="/gift-guide" exact component={GiftingGuide} />
            <Route path="/how-to/custom-tshirt-with-ai" exact component={HowToCustomTshirtAI} />
            <Route path="/how-to/personalized-gifts-ai" exact component={HowToPersonalizedGiftsAI} />
            <Route path="/how-to/custom-mug-with-ai" exact component={HowToCustomMugAI} />
            <Route path="/how-to/print-on-demand" exact component={HowToPrintOnDemand} />
            <Route path="/how-to/custom-merch-with-ai" exact component={HowToCustomMerchWithAI} />
            <Route path="/how-to/best-prompting-techniques-ai-merch" exact component={BestPromptingTechniquesAI} />
            <Route path="/compare/dtg-vs-dtf" exact component={CompareDTGVsDTF} />
            <Route path="/compare/printspaceai-vs-canva-for-merch" exact component={ComparePrintspaceAIVsCanvaMerch} />
            <Route path="/guides" exact component={GuidesHub} />
            <Route path="/what-is-printspaceai" exact component={WhatIsPrintSpaceAI} />
            <Route path="/shop" exact component={Shop} />
            <Route path="/shop/:designId" component={ShopDesignDetail} />
            <Route path="/creator-profile" exact component={CreatorProfile} />
            <Route path="/catalog" exact component={AllFurnitures} />
            <Route path="/products" exact component={Products} />
            <Route path="/category/:categoryId" exact component={FilteredProducts} />
            <Route path="/product/:slug" exact component={Product} />
            <Route path="/catalog-product/:slug" exact component={CatalogProduct} />
            <Route exact path="/login" component={Login} />
            <Route exact path="/forgotpassword" component={ForgotPass} />
            {/* <Route exact path="/forgot/password" component={ForgotPassword} /> */}
            <Route exact path="/register" component={Register} />
            <Route exact path="/verifyotp" component={VerifyOtp} />
            <Route exact path="/verifyotpforgot" component={VerifyOtpForgot} />
            <Route exact path="/contactus" component={ContactUs} />
            
            <Route exact path="/cart" component={Cart} />

            <Route exact path="/orderspage" component={OrdersPage} />

            <Route exact path="/checkout" component={Checkout} />
            <Route exact path="/token-checkout" component={TokenCheckout} />
            <Route exact path="/address" component={Address} />
            <Route exact path="/faq" component={Faq} />
            <Route exact path="/how-things-work" component={HowThingsWork} />
            {/* <Route exact path="/privacy" component={Privacy} /> */}
            <Route exact path="/tnc" component={Tnc} />

            {/* <Route path="/createdesign" exact component={CreateDesign} /> */}
            <Route path="/mydesigns" exact component={MyDesigns} />
            <Route path="/designertool/:blueprintId/:productSlug?" exact component={DesignerTool} />
            <Route path="/designertool" exact component={DesignerTool} />
          {role === "admin" ?  <Route path="/admin" exact component={AdminPage} />:null}
            <Route path="/privacy" exact component={Privacy} />
            <Route
              exact
              path="/register/complete"
              component={RegisterComplete}
            />
           
         
          </Switch>

          {/* <ContactUs /> */}

          <PromptHelperWidget />
          <Footer />
        </div>
      </div>
    </Suspense>
  );
};

export default App;
